import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import WalletUsageSection from "src/components/app/WalletUsageSection";
import Card from "src/components/app/Card/Card";
import BillingView from "src/components/app/lab-tests/BillingView";
import ProceedPaymentBottomSheet from "src/components/app/bottom-sheet/ProceedPaymentBottomSheet";
import Alert from "src/components/app/Alert";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { CoachData } from "src/types/appointment.types";
import { AlertTriangle } from "lucide-react";
import { initiateRazorpayPayment } from "src/helpers/razorPayHelper";
import { toast } from "sonner";
import { addPatientPlanRequest } from "src/redux/slices/patientSlice";
import { razorPayOrderIdRequest } from "src/redux/slices/razorPaySlice";
import * as actions from "src/redux/slices/appointmentsSlice";
import {
  BOOK_APPOINTMENT_PAYMENT_STATUS,
  PATIENT_PLAN_TYPE,
  APPOINTMENT_SLOT_BOOKING_STATUS,
} from "src/constants/common";
import { AddPatientPlanPayload } from "src/types/api/patient.types";
import { GetRazorpayOrderIdPayload } from "src/types/api/razorpay.types";
import analytics from "src/helpers/analytics";
import environment from "src/constants/environment";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import supportPrograms from "src/data/app/programs";

const ConsultationPaymentScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const coachKey = params["coach-key"];
  const coachData = useAppSelector((state) => state.appointments.coach);
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const bookingAmount = coachData?.price || coachData?.mrp || 0;
  const [finalAmountToPay, setFinalAmountToPay] = useState(bookingAmount);
  const [useWalletMoney, setUseWalletMoney] = useState<boolean>(false);
  const [walletUsage, setWalletUsage] = useState<number>(0);
  const dispatch = useAppDispatch();
  const patientDetails = useAppSelector((state) => state.auth.patient.data);
  const config =
    supportPrograms[supportProgramKey as keyof typeof supportPrograms];
  const isWalletValid = config?.config?.enableWallet;

  useEffect(() => {
    // Event: CONSULTATION_PAYMENT_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.CONSULTATION_PAYMENT_CHECKOUT_SCREEN);
  }, []);

  if (!coachData) {
    toast.error("Coach data not found in this screen");
    return null;
  }

  const trackSuccessPayment = () => {
    // Event: BOOK_COACH_PAYMENT_SUCCESS
    analytics.trackEvent(EVENT_NAMES.BOOK_COACH_PAYMENT_SUCCESS, {
      screen_name: SCREEN_NAMES.CONSULTATION_PAYMENT_CHECKOUT_SCREEN,
      support_program_key: supportProgramKey,
      final_amount: finalAmountToPay,
      ...coachData,
    });
  };

  const trackErrorPayment = () => {
    // Event: BOOK_COACH_PAYMENT_ERROR
    analytics.trackEvent(EVENT_NAMES.BOOK_COACH_PAYMENT_ERROR, {
      screen_name: SCREEN_NAMES.CONSULTATION_PAYMENT_CHECKOUT_SCREEN,
      support_program_key: supportProgramKey,
      final_amount: finalAmountToPay,
      ...coachData,
    });
  };

  const trackSuccessAddPatientPlan = (paymentId: string, orderId: string) => {
    // Event: CONSULTATION_PLAN_SUCCESS
    analytics.trackEvent(EVENT_NAMES.CONSULTATION_PLAN_SUCCESS, {
      screen_name: SCREEN_NAMES.CONSULTATION_PAYMENT_CHECKOUT_SCREEN,
      support_program_key: supportProgramKey,
      final_amount: finalAmountToPay,
      payment_id: paymentId,
      order_id: orderId,
    });
  };

  const trackErrorAddPatientPlan = (paymentId: string, orderId: string) => {
    // Event: CONSULTATION_PLAN_ERROR
    analytics.trackEvent(EVENT_NAMES.CONSULTATION_PLAN_ERROR, {
      screen_name: SCREEN_NAMES.CONSULTATION_PAYMENT_CHECKOUT_SCREEN,
      support_program_key: supportProgramKey,
      final_amount: finalAmountToPay,
      payment_id: paymentId,
      order_id: orderId,
    });
  };

  const addPatientPlan = async (
    paymentId: string,
    orderId: string,
    coachData: CoachData
  ) => {
    return new Promise((resolve, reject) => {
      const payload: AddPatientPlanPayload = {
        plan_master_id: coachData?.plan_master_id,
        transaction_id: paymentId,
        plan_package_duration_rel_id: coachData?.plan_package_duration_rel_id,
        device_type: "A",
        purchase_amount: Number(finalAmountToPay),
        receipt_data: "",
        plan_type: PATIENT_PLAN_TYPE.Individual,
        dev: environment.ENV !== "prod",
        order_id: orderId,
        payment_status: BOOK_APPOINTMENT_PAYMENT_STATUS.COMPLETED, // it will be always completed since this api is called after payment is successful
        slot_booking_process: APPOINTMENT_SLOT_BOOKING_STATUS.PENDING, // it will be always pending since this api is called after payment is successful.
        ...(useWalletMoney &&
          walletUsage > 0 && {
            wallet_amount: walletUsage * 100,
            wallet_payment: "Y",
          }),
      };
      dispatch(
        addPatientPlanRequest({
          resolve,
          reject,
          payload: payload,
        })
      );
    })
      .then((response: any) => {
        if (response?.code === 1) {
          // Event: CONSULTATION_PLAN_SUCCESS
          trackSuccessAddPatientPlan(paymentId, orderId);
          dispatch(actions.setFinalAmountPaid(Number(finalAmountToPay)));
          navigate(
            `/app/${supportProgramKey}/book-consultation/select-slot/${coachKey}`
          );
          toast.success(
            "Payment successful! Please select your appointment slot."
          );
        } else {
          // Event: CONSULTATION_PLAN_ERROR
          trackErrorAddPatientPlan(paymentId, orderId);
          toast.error(
            "Payment successful but plan activation failed. Please contact support."
          );
          throw new Error(
            "Payment successful but plan activation failed. Please contact support."
          );
        }
      })
      .catch((error) => {
        // Event: CONSULTATION_PLAN_ERROR
        trackErrorAddPatientPlan(paymentId, orderId);
        toast.error(
          "Payment successful but plan activation failed. Please contact support."
        );
        throw error;
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const callRazorpayPayment = async (razorpayOrderId: string) => {
    await initiateRazorpayPayment({
      amount: Number(finalAmountToPay),
      order_id: razorpayOrderId,
      name: "GoodFlip",
      description: `${coachData?.role} Consultation Payment`,
      features: {
        wallet_on_checkout: walletUsage > 0,
      },
      prefill: {
        name: patientDetails?.name,
        email: patientDetails?.email,
        contact: patientDetails?.contact_no,
      },
      onSuccess: async (response) => {
        // Step 3: Call add_patient_plan API
        trackSuccessPayment();
        await handleAddPatientPlan(
          response.razorpay_payment_id,
          razorpayOrderId,
          coachData
        );
      },
      onFailure: (error) => {
        // Event: BOOK_COACH_PAYMENT_ERROR
        trackErrorPayment();
        console.error("Payment failed:", error);
        console.error("Payment failed reason:", error?.message);
        toast.error("Payment failed. Please try again.");
        setIsProcessing(false);
      },
      onDismiss: () => {
        // Event: BOOK_COACH_PAYMENT_ERROR
        trackErrorPayment();
        setIsProcessing(false);
      },
    });
  };

  const getRazorpayOrderId = async (coachData: CoachData) => {
    return new Promise((resolve, reject) => {
      const payload: GetRazorpayOrderIdPayload = {
        amount: Number(finalAmountToPay),
        plan_master_id: coachData?.plan_master_id,
        plan_package_duration_rel_id: coachData?.plan_package_duration_rel_id,
        device_type: "A",
        purchase_amount: Number(finalAmountToPay),
        patient_address_rel_id: "",
        plan_type: PATIENT_PLAN_TYPE.Individual,
        dev: environment.ENV !== "prod",
        payment_status: BOOK_APPOINTMENT_PAYMENT_STATUS.COMPLETED,
        slot_booking_process: APPOINTMENT_SLOT_BOOKING_STATUS.PENDING, // it will be always pending since this api is called after payment is successful.
        ...(useWalletMoney &&
          walletUsage > 0 && {
            wallet_amount: walletUsage * 100,
            wallet_payment: "Y",
          }),
      };
      dispatch(
        razorPayOrderIdRequest({
          resolve,
          reject,
          payload: payload,
        })
      );
    })
      .then((response: any) => {
        const razorpayOrderId = response?.data;

        // if razorpay order id is not received, throw an error
        if (!razorpayOrderId) {
          throw new Error("Invalid order ID received");
        }

        // call razorpay payment
        callRazorpayPayment(razorpayOrderId);
      })
      .catch((error) => {
        console.error("Error getting razorpay order id:", error);
        console.error(
          "Error getting razorpay order id reason:",
          error?.message
        );
        throw error;
      });
  };

  const handleAddPatientPlan = async (
    paymentId: string,
    orderId: string,
    coachData: CoachData
  ) => {
    try {
      await addPatientPlan(paymentId, orderId, coachData);
    } catch (error: any) {
      // Event: CONSULTATION_PLAN_ERROR
      trackErrorAddPatientPlan(paymentId, orderId);
      console.error("Error adding patient plan:", error);
      console.error("Error adding patient plan reason:", error?.message);
      toast.error(
        "Payment successful but plan activation failed. Please contact support."
      );
    }
  };

  const handlePayment = async () => {
    // Event: BOOK_COACH_PAYMENT_CLICKED
    analytics.trackEvent(EVENT_NAMES.BOOK_COACH_PAYMENT_CLICKED, {
      screen_name: SCREEN_NAMES.CONSULTATION_PAYMENT_CHECKOUT_SCREEN,
      support_program_key: supportProgramKey,
      final_amount: finalAmountToPay,
      ...coachData,
    });
    setIsProcessing(true);
    try {
      await getRazorpayOrderId(coachData);
    } catch (error: any) {
      trackErrorPayment();
      console.error("Error in paid consultation:", error);
      toast.error(error?.message || "Failed to process payment");
    }
  };

  const calculateFinalAmountToPay = (usedWalletMoney: number) => {
    setFinalAmountToPay(Number(bookingAmount) - usedWalletMoney);
    if (usedWalletMoney > 0) {
      setUseWalletMoney(true);
      setWalletUsage(Number(usedWalletMoney));
    } else {
      setUseWalletMoney(false);
      setWalletUsage(0);
    }
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div className="flex flex-col h-full">
        <Header
          title={`Payment Details`}
          onBackClick={() => {
            navigate(`/app/${supportProgramKey}/book-consultation`);
          }}
        />
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 pb-20">
          <div className="max-w-md mx-auto">
            {/* Coach Info Card */}
            <div className="mb-6">
              <Card stackTitleAndDescription={true}>
                <div className="">
                  <div className="">
                    {/* <CoachProfilePicOrIcon coachKey={coachKey as HealthCoachType} /> */}
                    <div className="">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {coachData?.first_name} {coachData?.last_name}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {coachData?.role}
                      </p>
                      {coachData?.years_of_experience && (
                        <p className="text-xs text-gray-500 mt-1">
                          {coachData?.years_of_experience} year
                          {coachData?.years_of_experience > 1 ? "s" : ""}{" "}
                          experience
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Wallet Money Section - Using centralized component */}
            <div className="mb-6">
              {isWalletValid && (
                <WalletUsageSection
                  bookingAmount={bookingAmount}
                  discountAmount={0}
                  onUsedWalletMoney={calculateFinalAmountToPay}
                />
              )}
            </div>

            {/* Billing View */}
            <div className="mb-6">
              <h2 className="text-md font-semibold mb-2">Billing</h2>
              <Card stackTitleAndDescription={true}>
                <BillingView
                  billingTitle="Consultation Fee"
                  discountTitle="Discount on Consultation Fee"
                  orderTotal={String(bookingAmount || 0)}
                  finalPayableAmount={
                    Number(bookingAmount) - (useWalletMoney ? walletUsage : 0)
                  }
                  discount={0}
                  couponDiscount={0}
                  usedWalletMoney={useWalletMoney ? walletUsage : 0}
                />
              </Card>
            </div>

            {/* Information Box */}
            <div className="mb-6 flex flex-col gap-4">
              <Alert
                title="What's Next?"
                description="After successful payment, you'll be able to select your preferred appointment slot."
                variant="info"
                icon={<AlertTriangle />}
              />

              <Alert
                title="Disclaimer"
                description="On cancellation of the appointment, the refund amount will be credited to your wallet as care coins."
                variant="warning"
                icon={<AlertTriangle />}
              />
            </div>

            {/* About Coach */}
            {coachData?.about_me && (
              <div className="rounded-lg p-4 mb-6 border border-gray-200 bg-white">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  About {coachData?.first_name}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {coachData?.about_me}
                </p>
              </div>
            )}
          </div>
        </div>

        <ProceedPaymentBottomSheet
          finalAmountToPay={Number(finalAmountToPay || 0)}
          handleProceedToPayment={handlePayment}
          termsAccepted={null}
          isLoading={isProcessing}
        />
      </div>
    </Layout>
  );
};

export default ConsultationPaymentScreen;
